import type { PanelBlock, PanelMetric, PanelPlatform, Sums } from '../../api/panel'

// Formatação e rótulos do painel (pt-BR). Os cálculos espelham src/services/panel.ts.

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
const BRL0 = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })
const NUM = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 })
const NUM1 = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 })
const NUM2 = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export const METRIC_LABEL: Record<PanelMetric, string> = {
  spend: 'Investimento',
  conversions: 'Conversões',
  cost_per_conversion: 'CPA',
  revenue: 'Receita',
  roas: 'ROAS',
  clicks: 'Cliques',
  ctr: 'CTR',
  cpc: 'CPC',
  cpm: 'CPM',
  impressions: 'Impressões',
}

export const METRIC_TREND: Record<PanelMetric, 'higher' | 'lower' | 'neutral'> = {
  spend: 'neutral',
  conversions: 'higher',
  cost_per_conversion: 'lower',
  revenue: 'higher',
  roas: 'higher',
  clicks: 'higher',
  ctr: 'higher',
  cpc: 'lower',
  cpm: 'lower',
  impressions: 'higher',
}

export const PLATFORM_NAME: Record<PanelPlatform, string> = { google_ads: 'Google Ads', meta_ads: 'Meta Ads' }
export const PLATFORM_COLOR: Record<PanelPlatform, string> = { google_ads: '#4f46e5', meta_ads: '#0ea5e9' }
export const SOURCE_NAME: Record<string, string> = { google_ads: 'Google', meta_ads: 'Meta', ga4: 'GA4' }

export const BLOCK_LABEL: Record<PanelBlock, string> = {
  kpis: 'Indicadores',
  serie_diaria: 'Dia a dia',
  semanas: 'Semana a semana',
  campanhas: 'Campanhas',
  plataformas: 'Google × Meta',
  site: 'Site (GA4)',
  mercado: 'Mercado do nicho',
}

export function fmtMetric(m: PanelMetric, v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—'
  switch (m) {
    case 'spend':
    case 'revenue':
      return Math.abs(v) >= 10_000 ? BRL0.format(v) : BRL.format(v)
    case 'cost_per_conversion':
    case 'cpc':
    case 'cpm':
      return BRL.format(v)
    case 'roas':
      return `${NUM2.format(v)}x`
    case 'ctr':
      return `${NUM2.format(v)}%`
    case 'conversions':
      // O Google fraciona conversão (atribuição); abaixo de 100, mostra 1 casa.
      return Math.abs(v) < 100 ? NUM1.format(v) : NUM.format(v)
    default:
      return NUM.format(v)
  }
}

export const fmtNum = (v: number | null | undefined) => (v == null ? '—' : NUM.format(v))

export function fmtDelta(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—'
  const r = Math.round(v * 10) / 10
  if (r === 0) return '0%' // sem "-0%"
  return `${r > 0 ? '+' : ''}${NUM1.format(r)}%`
}

/** Cor da variação conforme o sentido "bom" da métrica. */
export function deltaClass(v: number | null | undefined, trend: 'higher' | 'lower' | 'neutral'): string {
  if (v == null || !Number.isFinite(v) || Math.abs(v) < 0.5 || trend === 'neutral') return 'text-slate-500'
  const good = trend === 'higher' ? v > 0 : v < 0
  return good ? 'text-emerald-600' : 'text-rose-600'
}

export const fmtDay = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}`
export const fmtDate = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}`
const MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']
export const fmtMonth = (m: string) => `${MONTHS[Number(m.slice(5, 7)) - 1] ?? m.slice(5, 7)}/${m.slice(2, 4)}`

export function deltaPct(cur: number | null, prev: number | null): number | null {
  return cur != null && prev != null && prev !== 0 ? ((cur - prev) / Math.abs(prev)) * 100 : null
}

/** Valor da métrica a partir das somas (mesmas fórmulas do servidor). */
export function metricOf(s: Sums, m: PanelMetric): number | null {
  switch (m) {
    case 'spend':
      return s.spend
    case 'conversions':
      return s.conversions
    case 'cost_per_conversion':
      return s.conversions > 0 ? s.spend / s.conversions : null
    case 'revenue':
      return s.conversionValue
    case 'roas':
      return s.spend > 0 ? s.conversionValue / s.spend : null
    case 'clicks':
      return s.clicks
    case 'ctr':
      return s.impressions > 0 ? (s.clicks / s.impressions) * 100 : null
    case 'cpc':
      return s.clicks > 0 ? s.spend / s.clicks : null
    case 'cpm':
      return s.impressions > 0 ? (s.spend / s.impressions) * 1000 : null
    case 'impressions':
      return s.impressions
  }
}

// ── Datas do seletor de período (o "hoje" é o de São Paulo, como no servidor) ──

const SP_DATE = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Sao_Paulo',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})
const DAY_MS = 86_400_000
const parse = (s: string) => new Date(`${s}T00:00:00.000Z`)
const ymd = (d: Date) => d.toISOString().slice(0, 10)
export const addDays = (s: string, n: number) => ymd(new Date(parse(s).getTime() + n * DAY_MS))
export const yesterdaySp = () => addDays(SP_DATE.format(new Date()), -1)

export type PeriodPreset = '7' | '14' | '30' | '90' | 'mes' | 'mes_passado' | 'custom'

export const PERIOD_LABEL: Record<PeriodPreset, string> = {
  '7': '7 dias',
  '14': '14 dias',
  '30': '30 dias',
  '90': '90 dias',
  mes: 'Este mês',
  mes_passado: 'Mês passado',
  custom: 'Personalizado',
}

export function presetRange(preset: PeriodPreset, custom: { from: string; to: string }): { from: string; to: string } {
  const y = yesterdaySp()
  if (preset === 'custom' && custom.from && custom.to) {
    const to = custom.to > y ? y : custom.to
    return { from: custom.from > to ? to : custom.from, to }
  }
  if (preset === 'mes') return { from: `${y.slice(0, 7)}-01`, to: y }
  if (preset === 'mes_passado') {
    const lastOfPrev = addDays(`${SP_DATE.format(new Date()).slice(0, 7)}-01`, -1)
    return { from: `${lastOfPrev.slice(0, 7)}-01`, to: lastOfPrev }
  }
  const n = Number(preset) || 30
  return { from: addDays(y, -(n - 1)), to: y }
}
